import { Flex, Stack, useBreakpointValue } from '@chakra-ui/react';
import { formatActivityType } from '@shinetools/ape-code-library';

import asBentoPage from 'common/bento/hoc/asBentoPage';
import Button from 'components/_core/Button';
import InformationSection, {
  InformationSectionItem,
} from 'features/Onboarding/components/InformationSection';
import OBBackButton from 'features/Onboarding/components/OBBackButton';
import OBNavigation from 'features/Onboarding/components/OBNavigation/OBNavigation';
import { formatAddressMultiLine } from 'helpers/address';
import { formatDate, PPP } from 'helpers/date';
import { formatSIRET } from 'helpers/siret';

import Header from '../../../../components/Header';
import { Context } from '../../machine/model';
import locales from './locales';

interface InfoVerificationProps {
  company: Context['companyProfile'];
  onValidate: () => void;
  onEditIdentityInfo: () => void;
  onEditLegalInfo: () => void;
}

const InfoVerification = asBentoPage<InfoVerificationProps>(
  ({
    company,
    Layout,
    onEditIdentityInfo,
    onEditLegalInfo,
    onPrev,
    onValidate,
  }) => {
    const isDesktop = useBreakpointValue({
      base: false,
      md: true,
    });

    const legalInformationItems: InformationSectionItem[] = [
      [
        locales.registrationDate,
        formatDate(new Date(company.registrationDate ?? ''), PPP),
      ],
      [locales.siret, formatSIRET(company.legalRegistrationNumber)],
      [locales.activity, formatActivityType(company.activityType)],
      [locales.nafCode, company.activityType],
    ];

    const identityInformationItems: InformationSectionItem[] = [
      [locales.legalName, company.legalName],
      [locales.tradeName, company.tradeName],
      [locales.address, formatAddressMultiLine(company)],
    ];

    const renderSections = () => {
      return (
        <>
          <InformationSection
            iconName="store"
            items={identityInformationItems}
            onEdit={onEditIdentityInfo}
            title={locales.identity}
          />
          <InformationSection
            iconName="company"
            items={legalInformationItems}
            onEdit={onEditLegalInfo}
            title={locales.legalInformation}
          />
        </>
      );
    };

    return (
      <Layout
        data-testid="info-verification"
        header={<OBBackButton onClick={onPrev} />}
        nav={<OBNavigation onPrev={onPrev} />}
      >
        <Flex direction="column" height={{ base: 'full', md: 'auto' }}>
          <Header description={locales.description} title={locales.title} />

          <Stack spacing="space-24">
            {renderSections()}

            <Button
              isExpanded
              margin={isDesktop ? undefined : 'auto'}
              marginY="space-16"
              onClick={onValidate}
              type="submit"
            >
              {locales.validate}
            </Button>
          </Stack>
        </Flex>
      </Layout>
    );
  },
);

export default InfoVerification;
